import { Drawer, IconButton, Typography, Box, List, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { OrderNotification, useOrderNotifications } from '@shared/contexts/OrderNotificationsContexts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useMemo, useState } from 'react';
import { OrderFieldFilter } from './OrderFieldFilter';
import { createFilterConfig } from './orderFilterConfigs';
import { NotificationItem } from './NotificationItem';
import { useOrderFilters } from './useOrderFilter';

const DRAWER_WIDTH = 400;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
interface OrderNotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  isDTUser?: boolean;
}

export const OrderNotificationsPanel = ({ open, onClose, isDTUser }: OrderNotificationsPanelProps) => {
  const { notifications, markAsRead, markAllAsRead, clearNotifications, isSoundEnabled, toggleSound, clearNotification } = useOrderNotifications();

  const allOrders = useMemo(() => notifications.map(n => n.order), [notifications]);
  const [expandedFilters, setExpandedFilters] = useState<Record<string, boolean>>({});
  const orderTypeFilterConfig = useMemo(() => createFilterConfig(allOrders, 'order_type', false, 'Order Type'), [allOrders]);
  const userAccountFilterConfig = useMemo(() => createFilterConfig(allOrders, 'account_name', true, 'Organization'), [allOrders]);

  const dtFilterConfigs = useMemo(() => [userAccountFilterConfig], [userAccountFilterConfig]);
  const traderFilterConfigs = useMemo(() => [orderTypeFilterConfig], [orderTypeFilterConfig]);

  const { filteredNotifications, handleFilterChange, handleSelectAll, selectedFilters } = useOrderFilters({
    notifications,
    filterConfigs: isDTUser ? dtFilterConfigs : traderFilterConfigs,
  });
  const unreadCount = useMemo(() => {
    return filteredNotifications.filter(n => !n.read).length;
  }, [filteredNotifications]);

  const onNotificationClick = (notification: OrderNotification) => {
    markAsRead(notification.id);
  };

  const handleClose = () => {
    onClose();
    markAllAsRead();
  };

  const toggleFilterExpanded = (filterKey: string) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  return (
    <StyledDrawer anchor="right" open={open} onClose={handleClose}>
      <DrawerHeader>
        <Box display="flex" alignItems="center" gap={1}>
          <NotificationsIcon />
          <Typography variant="h6">Order Notifications</Typography>
          <IconButton onClick={toggleSound} size="small">
            {isSoundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton>
          {unreadCount > 0 && (
            <Typography variant="h6" sx={{ ml: 1 }}>
              ({unreadCount} unread)
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} color="inherit">
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <Box p={2}>
        <Box display="flex" gap={1} mb={2}>
          <Button startIcon={<DeleteSweepIcon />} onClick={clearNotifications} disabled={notifications.length === 0} size="small" color="error">
            Clear all
          </Button>
        </Box>

        {isDTUser
          ? dtFilterConfigs.map(config => (
              <OrderFieldFilter
                key={config.field}
                config={config}
                selectedValues={selectedFilters[config.field] || []}
                onFilterChange={(value: string) => handleFilterChange(config.field, value)}
                onSelectAll={() => handleSelectAll(config.field)}
                isExpanded={expandedFilters[config.field]}
                onToggleExpanded={() => toggleFilterExpanded(config.field)}
              />
            ))
          : traderFilterConfigs.map(config => (
              <OrderFieldFilter
                key={config.field}
                config={config}
                selectedValues={selectedFilters[config.field] || []}
                onFilterChange={(value: string) => handleFilterChange(config.field, value)}
                onSelectAll={() => handleSelectAll(config.field)}
                isExpanded={expandedFilters[config.field]}
                onToggleExpanded={() => toggleFilterExpanded(config.field)}
              />
            ))}

        <List>
          {filteredNotifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onNotificationClick={onNotificationClick}
              onClearNotification={clearNotification}
              isDTUser={isDTUser}
            />
          ))}
        </List>
      </Box>
    </StyledDrawer>
  );
};
